import React from "react";
import PropTypes from "prop-types";
import { Form as FormikForm, InputField, CheckboxField } from "components/common_components/Formik";
import * as Yup from "yup";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import FormGroup from "@material-ui/core/FormGroup";
import Grid from "@material-ui/core/Grid";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import NoEncryptionOutlinedIcon from "@material-ui/icons/NoEncryptionOutlined";
import { makeStyles } from "@material-ui/core/styles";
import { Field } from "formik";
import Icon from "components/common_components/Icon";
import Button from "components/common_components/Button";

const useStyles = makeStyles((theme) => ({
    formControl: {
        margin: "unset",
        paddingTop: theme.spacing(3),
    },
    buttons: {
        marginTop: theme.spacing(2),
    },
    button: {
        margin: theme.spacing(0, 1, 0, 0),
    },
    authorizeButton: {
        margin: theme.spacing(0, 1, 0, 0),
    },
    authorizedButton: {
        margin: theme.spacing(0, 1, 0, 0),
        backgroundColor: "green !important",
        color: "white !important",
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(0.5, 1),
        borderRadius: 5,
    },
    notAuthorizedButton: {
        margin: theme.spacing(0, 1, 0, 0),
        backgroundColor: "red !important",
        color: "white !important",
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(0.5, 1),
        borderRadius: 5,
    },
}));

function Form({ data, resources, mode, onEdit, onAuthorize, ...rest }) {
    const classes = useStyles();
    const readOnly = mode === "detail";
    const { modules } = resources;

    const initialValues = { ...data, modules: data.modules.reduce((acc, m) => ({ ...acc, [m.id]: true }), {}) };

    const validationSchema = Yup.object({
        name: Yup.string().required("Required field"),
        phoneNumber: Yup.string().required("Required field"),
        email: Yup.string().required("Required field").email("Incorrect email"),
        password:
            mode === "add"
                ? Yup.string().required("Required field").min(6, "At least 6 characters")
                : Yup.string().notRequired().nullable(),
        isActive: Yup.string().required("Required field"),
        isAdmin: Yup.string().required("Required field"),
        hasRestrictedAccess: Yup.string().required("Required field"),
    });

    return (
        <FormikForm validateOnChange initialValues={initialValues} validationSchema={validationSchema} showButtons={!readOnly} {...rest}>
            <div className="layout">
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">General Information</FormLabel>
                            <FormGroup row>
                                <InputField disabled={readOnly} fieldName="name" label="Name *" />
                                <InputField disabled={readOnly} fieldName="phoneNumber" label="Phone Number *" />
                                <InputField
                                    disabled={mode !== "add"}
                                    fieldName="email"
                                    label="Email *"
                                    helperText="e.g myname@domain.com"
                                    lowercase
                                />
                                {mode === "add" && (
                                    <InputField
                                        type="password"
                                        fieldName="password"
                                        label="Password *"
                                        helperText="It must have at least 6 characters"
                                    />
                                )}
                            </FormGroup>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl component="fieldset" className={classes.formControl}>
                            <FormLabel component="legend">Basic Access</FormLabel>
                            <FormGroup>
                                <CheckboxField readOnly={readOnly} fieldName="isActive" label="Is Active" />
                                <CheckboxField readOnly={readOnly} fieldName="isAdmin" label="Is Admin" />
                                <CheckboxField readOnly={readOnly} fieldName="hasRestrictedAccess" label="Has Restricted Access" />
                                <CheckboxField readOnly={readOnly} fieldName="canManagePayments" label="Can Manage Payments" />
                                <CheckboxField readOnly={readOnly} fieldName="canManageCalendar" label="Can Manage Calendar" />
                                <CheckboxField readOnly={readOnly} fieldName="canChatWithClients" label="Can Chat with Clients" />
                                <CheckboxField readOnly={readOnly} fieldName="canUsePos" label="POS" />
                                <CheckboxField readOnly={readOnly} fieldName="canUseInvoicing" label="Invoicing" />
                            </FormGroup>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl component="fieldset" className={classes.formControl}>
                            <FormLabel component="legend">Module Access</FormLabel>
                            <FormGroup>
                                {modules.map(({ id, name }, index) => (
                                    <CheckboxField key={index} readOnly={readOnly} fieldName={`modules.${id}`} label={name} />
                                ))}
                            </FormGroup>
                        </FormControl>
                    </Grid>
                    {mode !== "add" && (
                        <Grid item xs={12} md={2}>
                            <FormControl component="fieldset" className={classes.formControl}>
                                <FormLabel component="legend">Authorization</FormLabel>
                                <FormGroup>
                                    <Field name="isAuthorized">
                                        {({ field }) =>
                                            field.value ? (
                                                <>
                                                    <div className={classes.authorizedButton}>
                                                        <VerifiedUserIcon />
                                                        <span>Authorized</span>
                                                    </div>
                                                </>
                                            ) : readOnly ? (
                                                <div className={classes.notAuthorizedButton}>
                                                    <NoEncryptionOutlinedIcon />
                                                    <span>Not Authorized</span>
                                                </div>
                                            ) : (
                                                <Button secondary onClick={onAuthorize}>
                                                    Authorize
                                                </Button>
                                            )
                                        }
                                    </Field>
                                </FormGroup>
                            </FormControl>
                        </Grid>
                    )}
                </Grid>
            </div>
            {readOnly && (
                <div className="button-group">
                    <Button secondary outline onClick={rest.onCancel}>
                        <Icon name="arrow" size="md" color="secondary" />
                        <span>Back</span>
                    </Button>
                    <Button secondary onClick={onEdit}>
                        <Icon name="pencil" size="md" color="white" />
                        <span>Edit</span>
                    </Button>
                </div>
            )}
        </FormikForm>
    );
}

Form.propTypes = {
    data: PropTypes.shape({
        name: PropTypes.string,
        phoneNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        email: PropTypes.string,
        password: PropTypes.string,
        isActive: PropTypes.bool,
        isAdmin: PropTypes.bool,
        hasRestrictedAccess: PropTypes.bool,
        isAuthorized: PropTypes.bool,
        modules: PropTypes.arrayOf(PropTypes.shape({})),
        canManagePayments: PropTypes.bool,
        canManageCalendar: PropTypes.bool,
        canChatWithClients: PropTypes.bool,
        canUsePos: PropTypes.bool,
        canUseInvoicing: PropTypes.bool,
    }),
    resources: PropTypes.shape({
        modules: PropTypes.arrayOf(
            PropTypes.shape({
                code: PropTypes.string,
                name: PropTypes.string,
                description: PropTypes.string,
            })
        ),
    }),
    mode: PropTypes.oneOf(["add", "detail", "update"]).isRequired,
    onEdit: PropTypes.func,
    onAuthorize: PropTypes.func,
};

Form.defaultProps = {
    data: {
        name: "",
        phoneNumber: "",
        email: "",
        password: "",
        isActive: true,
        isAdmin: false,
        hasRestrictedAccess: false,
        isAuthorized: false,
        modules: [],
        canManagePayments: false,
        canManageCalendar: false,
        canChatWithClients: false,
        canUsePos: false,
        canUseInvoicing: false,
    },
    resources: {
        modules: [],
    },
    onEdit: () => {},
    onAuthorize: () => {},
};

export default Form;
